import { useState, useEffect, useRef, BaseSyntheticEvent } from "react";
import { filter, size, some, omit, isEmpty, map } from "lodash";

import Attachments from "./Attachments";
import Modal from "../../shared/Modal";
import { useCreateComment, useUpdateComment } from "./api";

import styles from "./ReplyComposer.module.css";
import { Controller, FormProvider, useForm } from "react-hook-form";
import FormActions from "../../shared/form/fields/FormActions";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons";
import Editor from "../../shared/markdownEditor/MarkdownEditor";
import {
  ActivityReply,
  FileAttachment,
  ImageAttachment,
} from "../../../@types/activities.ts";
import ReplyQuote from "../activity/replies/ReplyQuote.tsx";

const filterRemoved = (items) =>
  filter(items, (item) => item.state != "removed");

interface ReplyComposerTypes {
  activityId: string;
  type?: "replies" | "answers";
  reply?: ActivityReply;
  isOpen?: boolean;
  selectedTab?: string;
  quoteData?: ActivityReply | null;
  saveCallback?: (
    data: Omit<ActivityReply, "author"> & {
      i: { can: ActivityReply["can"] };
      author: Omit<ActivityReply["author"], "images"> & {
        display_name: string;
        images: { width: number; height: number; url: string }[];
      };
      attachments: ((FileAttachment | ImageAttachment) & {
        sources: { url: string; width: number; height: number }[];
        type: string;
      })[];
    },
  ) => void;
  cancelCallback?: () => void;
  setQuoteData?: (reply: ActivityReply | null) => void;
  className?: string;
}

type FormData = {
  content: string;
  files: FileAttachment[];
  images: ImageAttachment[];
};

function getAttachmentsByType(
  type: string,
  attachments?: ActivityReply["attachments"],
) {
  if (isEmpty(attachments)) return [];

  return map(
    filter(attachments, { type }) as (FileAttachment | ImageAttachment)[],
    (file: FileAttachment | ImageAttachment) => ({
      name: file.original_filename,
      ...file,
    }),
  );
}

export default function ReplyComposer({
  type = "replies",
  activityId,
  reply,
  isOpen = false,
  selectedTab,
  quoteData,
  saveCallback,
  cancelCallback,
  className,
  setQuoteData,
}: ReplyComposerTypes) {
  const editorRef = useRef<{ focus: () => void }>();
  const [open, setOpen] = useState(isOpen);
  const [fullscreen, setFullscreen] = useState(false);

  const methods = useForm<FormData>({
    defaultValues: {
      content: reply?.original_content || "",
      files:
        (getAttachmentsByType(
          "file",
          reply?.attachments,
        ) as FileAttachment[]) || [],
      images:
        (getAttachmentsByType(
          "image",
          reply?.attachments,
        ) as ImageAttachment[]) || [],
    },
  });
  const { handleSubmit, watch, control, reset } = methods;

  // Update local quoteData when the prop changes
  useEffect(() => {
    if (quoteData && !isOpen) {
      setOpen(true);
    }
  }, [quoteData, isOpen, setOpen]);

  const openComposer = () => {
    setOpen(true);
    setTimeout(() => {
      editorRef.current?.focus();
    });
  };
  const closeComposer = (reset?) => {
    if (reset) {
      reset();
    }
    setQuoteData?.(null);
    setOpen(false);
  };

  // const openFullscreen = () => {
  //   setFullscreen(true);
  // };
  const closeFullscreen = () => {
    setFullscreen(false);
  };

  const onSuccess = (result) => {
    if (saveCallback) {
      saveCallback(result);
    }

    reset();
    closeComposer();
  };

  const onCancel = () => {
    if (cancelCallback) {
      cancelCallback();
    }
    setQuoteData?.(null);
    reset();
    closeComposer();
  };

  const onError = () => {
    toastr.error(I18n.t("generic_error"));
  };

  const { mutate: createComment, isLoading: isCreating } = useCreateComment({
    onSuccess,
    onError,
  });
  const { mutate: updateComment, isLoading: isUpdating } = useUpdateComment({
    onSuccess,
    onError,
  });

  async function submit(data: FormData, e: BaseSyntheticEvent | undefined) {
    e?.preventDefault();

    const body = {
      ...omit(data, ["files", "images"]),
      files: map(filterRemoved(data.files), (file) => file.id),
      images: map(filterRemoved(data.images), (file) => file.id),
    };

    if (reply) {
      return updateComment({
        activityId: activityId,
        replyId: reply.id,
        type,
        ...body,
      });
    } else {
      const quotedReplyId = quoteData ? { quotedReplyId: quoteData.id } : {};

      return createComment({
        activityId: activityId,
        type,
        ...body,
        ...quotedReplyId,
      });
    }
  }

  // To react to external isOpen prop changes
  useEffect(() => {
    isOpen ? openComposer() : closeComposer();
  }, [isOpen]);

  const files = watch("files");
  const images = watch("images");
  const fileProcessing = some(files, { state: "uploading" });
  const imageProcessing = some(images, { state: "uploading" });

  const handleCancelQuote = (e) => {
    e.preventDefault();
    setQuoteData?.(null);
  };

  return (
    <div className={className}>
      {open ? (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submit)}>
            {quoteData && (
              <ReplyQuote
                {...quoteData}
                handleCancelQuote={handleCancelQuote}
              />
            )}
            {fullscreen ? (
              <Modal
                static
                fullscreen
                onClose={closeFullscreen}
                title={I18n.t(`js.activitystreams.${type}.author_hint`)}
              >
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <Editor
                      {...omit(field, ["ref"])}
                      editorRef={editorRef}
                      enableTixxtMentions
                    />
                  )}
                />
              </Modal>
            ) : (
              <div className="flex gap-2">
                {!reply && Preload.current_member && (
                  <div className="member-image shrink-0 self-start">
                    <img
                      className="w-10 h-10"
                      src={Preload.current_member.images["40x40@2"]}
                      alt={Preload.current_member.displayName}
                    />
                  </div>
                )}
                <div className="flex flex-col">
                  <div className="min-h-48 grow">
                    <Controller
                      name="content"
                      control={control}
                      render={({ field }) => (
                        <Editor
                          {...omit(field, ["ref"])}
                          editorRef={editorRef}
                          enableTixxtMentions
                        />
                      )}
                    />
                  </div>
                  <Attachments
                    selectedTab={selectedTab}
                    filesCount={size(filterRemoved(files))}
                    imagesCount={size(filterRemoved(images))}
                  />
                </div>
              </div>
            )}
            <FormActions
              className="mt-2"
              onCancel={onCancel}
              saveText={I18n.t(`js.activitystreams.${type}.submit`)}
              saveIconProps={
                isUpdating || isCreating || fileProcessing || imageProcessing
                  ? {
                      icon: faSpinner,
                      spin: true,
                    }
                  : undefined
              }
              saveDisabled={
                isUpdating || isCreating || fileProcessing || imageProcessing
              }
            />
          </form>
        </FormProvider>
      ) : (
        <div className="flex gap-2">
          {!reply && Preload.current_member && (
            <div className="member-image shrink-0 self-start">
              <img
                className="w-10 h-10"
                src={Preload.current_member.images["40x40@2"]}
                alt={Preload.current_member.displayName}
              />
            </div>
          )}
          <button
            key="dummy"
            className={`ReplyComposer object-content-input form-control text-gray-400 text-left ${styles.closed}`}
            onClick={openComposer}
          >
            <span className="placeholder">
              {I18n.t(`js.activitystreams.${type}.author_hint`)}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
